(() => {
  const focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const productCache = new Map();

  const toBoolean = (value) => value === true || value === 'true';

  const splitTags = (tags) => {
    if (Array.isArray(tags)) return tags.map((tag) => String(tag).trim()).filter(Boolean);
    return String(tags || '')
      .split(/,\s*|\s+/)
      .map((tag) => tag.trim())
      .filter(Boolean);
  };

  const formatMoney = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';

    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getProductTimestamp = (value) => {
    const time = Date.parse(value || '');
    return Number.isFinite(time) ? time : 0;
  };

  const fetchCollectionProducts = async (handle) => {
    if (!handle) return [];
    if (productCache.has(handle)) return productCache.get(handle);

    const request = fetch(`/collections/${encodeURIComponent(handle)}/products.json?limit=250`)
      .then((response) => {
        if (!response.ok) throw new Error(`Collection products failed: ${response.status}`);
        return response.json();
      })
      .then((data) => data.products || [])
      .catch((error) => {
        console.warn('Katredar category story products could not be loaded.', error);
        return [];
      });

    productCache.set(handle, request);
    return request;
  };

  const buildStoryProducts = (products, card, config) => {
    const limit = Number(config.limit || card.dataset.categoryStoryLimit || 5);
    const windowHours = Number(config.windowHours || 24);
    const cutoff = Date.now() - windowHours * 60 * 60 * 1000;
    const filterTag = card.dataset.categoryStoryFilterTag || '';
    const manualTag = config.manualTag || '';
    const recentCheck = config.recentCheck !== false;
    const manualTagBypassWindow = config.manualTagBypassWindow !== false;
    const matches = [];

    products.forEach((product) => {
      if (matches.length >= limit) return;

      const tags = splitTags(product.tags);
      if (filterTag && !tags.includes(filterTag)) return;

      const publishedAt = getProductTimestamp(product.published_at);
      const createdAt = getProductTimestamp(product.created_at);
      const hasManualStoryTag = manualTag && tags.includes(manualTag);
      const isRecent =
        !recentCheck ||
        publishedAt >= cutoff ||
        createdAt >= cutoff ||
        (hasManualStoryTag && manualTagBypassWindow);

      if (!isRecent) return;

      const image = product.images?.[0]?.src || product.image?.src || '';
      const variantPrice = product.variants?.[0]?.price;

      matches.push({
        title: product.title || '',
        url: product.handle ? `/products/${product.handle}` : '#',
        price: formatMoney(variantPrice),
        image,
        imageAlt: product.title || '',
        createdAt: product.created_at || '',
        publishedAt: product.published_at || '',
      });
    });

    return matches;
  };

  const initRail = (section) => {
    const track = section.querySelector('[data-category-rail-track]');
    const previousButton = section.querySelector('[data-category-rail-prev]');
    const nextButton = section.querySelector('[data-category-rail-next]');

    if (track && previousButton && nextButton) {
      const updateButtons = () => {
        const maxScroll = track.scrollWidth - track.clientWidth;
        const current = track.scrollLeft;
        const hasOverflow = maxScroll > 2;

        previousButton.disabled = !hasOverflow || current <= 2;
        nextButton.disabled = !hasOverflow || current >= maxScroll - 2;
      };

      const scrollByStep = (direction) => {
        const firstItem = track.querySelector('.katredar-home-category-rail__item');
        const itemWidth = firstItem ? firstItem.getBoundingClientRect().width : 160;
        const gap = parseFloat(window.getComputedStyle(track).columnGap || '0');
        const distance = Math.max(itemWidth + gap, track.clientWidth * 0.58);

        track.scrollBy({
          left: direction * distance,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      };

      previousButton.addEventListener('click', () => scrollByStep(-1));
      nextButton.addEventListener('click', () => scrollByStep(1));
      track.addEventListener('scroll', () => window.requestAnimationFrame(updateButtons), { passive: true });
      window.addEventListener('resize', updateButtons);
      updateButtons();
    }

    initStories(section);
  };

  const initStories = (section) => {
    const modal = section.querySelector('[data-category-story-modal]');
    const dataScript = section.querySelector('[data-category-story-data]');
    const storyLinks = section.querySelectorAll('[data-category-story-card-link]');

    if (!modal || !dataScript) return;

    let data;
    try {
      data = JSON.parse(dataScript.textContent || '{}');
    } catch (error) {
      console.warn('Katredar category story data could not be parsed.', error);
      return;
    }

    const groups = new Map((data.groups || []).map((group) => [group.key, group]));
    const config = {
      windowHours: Number(data.windowHours || section.dataset.categoryStoryWindowHours || 24),
      recentCheck: data.recentCheck !== false && section.dataset.categoryStoryRecentCheck !== 'false',
      limit: Number(data.limit || section.dataset.categoryStoryLimit || 5),
      manualTag: data.manualTag || section.dataset.categoryStoryManualTag || '',
      manualTagBypassWindow:
        data.manualTagBypassWindow !== false && section.dataset.categoryStoryBypassWindow !== 'false',
    };
    const image = modal.querySelector('[data-category-story-image]');
    const category = modal.querySelector('[data-category-story-category]');
    const title = modal.querySelector('[data-category-story-product-title]');
    const price = modal.querySelector('[data-category-story-price]');
    const link = modal.querySelector('[data-category-story-product-link]');
    const closeButton = modal.querySelector('[data-category-story-close]');
    const previousButton = modal.querySelector('[data-category-story-prev]');
    const nextButton = modal.querySelector('[data-category-story-next]');
    const progress = modal.querySelector('[data-category-story-progress]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let activeGroup;
    let activeIndex = 0;
    let timer;
    let lastFocusedElement;
    let touchStartX = 0;

    const render = (index) => {
      if (!activeGroup?.products?.length) return;

      window.clearTimeout(timer);
      activeIndex = index;
      const product = activeGroup.products[activeIndex];

      category.textContent = activeGroup.title || '';
      image.src = product.image || '';
      image.alt = product.imageAlt || product.title || '';
      title.textContent = product.title || '';
      price.textContent = product.price || '';
      link.href = product.url;
      link.textContent = data.buttonLabel || 'Ürünü İncele';

      progress.querySelectorAll('[data-category-story-segment]').forEach((segment, segmentIndex) => {
        segment.classList.toggle('is-complete', segmentIndex < activeIndex);
        segment.classList.toggle('is-active', segmentIndex === activeIndex);
      });

      if (!reduceMotion.matches && activeGroup.products.length > 1) {
        timer = window.setTimeout(next, 5500);
      }
    };

    const open = (group) => {
      if (!group?.products?.length) return;

      activeGroup = group;
      lastFocusedElement = document.activeElement;
      progress.innerHTML = group.products
        .map((_, index) => `<span class="katredar-category-story-modal__progress-segment" data-category-story-segment="${index}"></span>`)
        .join('');
      modal.hidden = false;
      document.body.classList.add('katredar-category-story-open');
      render(0);
      closeButton.focus();
    };

    const close = () => {
      window.clearTimeout(timer);
      modal.hidden = true;
      document.body.classList.remove('katredar-category-story-open');
      lastFocusedElement?.focus?.();
    };

    function next() {
      if (!activeGroup?.products?.length) return;
      render((activeIndex + 1) % activeGroup.products.length);
    }

    function previous() {
      if (!activeGroup?.products?.length) return;
      render((activeIndex - 1 + activeGroup.products.length) % activeGroup.products.length);
    }

    const trapFocus = (event) => {
      if (event.key !== 'Tab' || modal.hidden) return;

      const focusable = Array.from(modal.querySelectorAll(focusableSelector)).filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const bindStoryLinks = () => {
      storyLinks.forEach((trigger) => {
        if (trigger.dataset.categoryStoryBound === 'true') return;
        trigger.dataset.categoryStoryBound = 'true';

        trigger.addEventListener('click', (event) => {
          const group = groups.get(trigger.dataset.categoryStoryKey);
          if (!group?.products?.length) return;

          event.preventDefault();
          open(group);
        });
      });
    };

    const enhanceClientStories = async () => {
      const cards = Array.from(section.querySelectorAll('[data-category-story-card]')).filter(
        (card) => card.dataset.categoryStoryCollection
      );
      if (!cards.length) return;

      const collectionHandles = [...new Set(cards.map((card) => card.dataset.categoryStoryCollection).filter(Boolean))];
      const collectionProducts = new Map();

      await Promise.all(
        collectionHandles.map(async (handle) => {
          collectionProducts.set(handle, await fetchCollectionProducts(handle));
        })
      );

      cards.forEach((card) => {
        const key = card.dataset.categoryStoryKey;
        if (!key || groups.get(key)?.products?.length) return;

        const products = buildStoryProducts(collectionProducts.get(card.dataset.categoryStoryCollection) || [], card, config);
        if (!products.length) return;

        groups.set(key, {
          key,
          title: card.dataset.categoryStoryTitle || '',
          collectionUrl: card.querySelector('a[href]')?.href || '',
          products,
        });

        card.classList.add('katredar-home-category-rail__item--has-story');
        if (section.dataset.categoryStoryAnimateRing !== 'false') {
          card.classList.add('katredar-home-category-rail__item--story-animate');
        }
      });
    };

    bindStoryLinks();
    enhanceClientStories().then(bindStoryLinks);

    closeButton.addEventListener('click', close);
    previousButton.addEventListener('click', previous);
    nextButton.addEventListener('click', next);

    modal.addEventListener('click', (event) => {
      if (event.target === modal) close();
    });

    modal.addEventListener(
      'touchstart',
      (event) => {
        touchStartX = event.changedTouches[0]?.clientX || 0;
      },
      { passive: true }
    );

    modal.addEventListener(
      'touchend',
      (event) => {
        const touchEndX = event.changedTouches[0]?.clientX || 0;
        const delta = touchEndX - touchStartX;
        if (Math.abs(delta) < 42) return;
        if (delta < 0) next();
        else previous();
      },
      { passive: true }
    );

    document.addEventListener('keydown', (event) => {
      if (modal.hidden) return;
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowRight') next();
      if (event.key === 'ArrowLeft') previous();
      trapFocus(event);
    });
  };

  const init = () => {
    document.querySelectorAll('[data-category-story-section]').forEach(initRail);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
